import { Clapperboard, Megaphone, Disc3, Star, Trophy, Mic2, Landmark, MonitorPlay, Clock, ShieldCheck, EyeOff, HardHat, Utensils, ArrowRight, BadgeCheck } from "lucide-react";
import { QuoteButton } from "@/components/QuoteButton";
import { useSiteImage } from "@/context/ContentContext";
import { useReveal, Seo } from "@/lib/seo";

const Q = { service: "Traiteur uniquement", eventType: "Production, tournage ou spectacle" };

const CLIENTS = [
  { icon: Clapperboard, title: "Productions cinématographiques et télévisuelles", kw: "Restauration tournage cinéma" },
  { icon: Megaphone, title: "Tournages publicitaires", kw: "Catering tournage" },
  { icon: Disc3, title: "Maisons de disque et sorties d'album", kw: "Traiteur sortie album" },
  { icon: Star, title: "Artistes et personnalités", kw: "Catering artiste · traiteur personnalité" },
  { icon: Trophy, title: "Sportifs professionnels et équipes sportives", kw: "Catering sportif · traiteur équipe sportive" },
  { icon: Mic2, title: "Spectacles et concerts", kw: "Catering événement musical" },
  { icon: Landmark, title: "Événements institutionnels et manifestations publiques", kw: "Catering manifestation" },
  { icon: MonitorPlay, title: "Plateaux TV et équipes techniques", kw: "Traiteur plateau TV" },
];

const OFFERS = [
  { title: "Catering plateau pour équipes techniques et artistiques", text: "Des repas complets et équilibrés pour tenir les longues journées de tournage, de répétition ou de montage." },
  { title: "Buffets et repas sur site", text: "Buffets chauds ou froids et repas servis, installés au plus près de vos équipes." },
  { title: "Service discret et personnalisé pour les personnalités", text: "Loges, green rooms, espaces VIP : un service attentif, sans bruit, dans le respect de la confidentialité." },
  { title: "Respect des horaires stricts", text: "Votre planning de production fait foi : les services sont calés sur votre feuille de service." },
  { title: "Adaptation aux régimes alimentaires et exigences spécifiques", text: "Halal, végétarien, allergies, demandes spécifiques des artistes ou des équipes : nous adaptons chaque repas." },
  { title: "Livraison et installation sur site", text: "Studio, plateau, salle de concert, stade ou lieu de tournage : nous livrons et installons sur place." },
];

const WHY = [
  { icon: ShieldCheck, title: "Fiabilité", text: "Un interlocuteur unique et des engagements tenus." },
  { icon: Clock, title: "Ponctualité", text: "Des services calés à la minute sur votre planning." },
  { icon: EyeOff, title: "Discrétion absolue", text: "Une discrétion absolue autour des artistes et des tournages." },
  { icon: HardHat, title: "Expérience terrain", text: "Depuis 2018 sur le terrain de l'événementiel." },
  { icon: BadgeCheck, title: "Halal disponible", text: "Notre offre principale, avec option non halal sur demande." },
  { icon: Utensils, title: "Cuisine variée", text: "Africaine, antillaise et française, pour toutes les équipes." },
];

function Viewfinder({ src, alt }) {
  return (
    <div className="relative overflow-hidden bg-[#2C2C2C]">
      <img src={src} alt={alt} className="aspect-[16/10] w-full object-cover opacity-90" />
      {["left-4 top-4 border-l-2 border-t-2", "right-4 top-4 border-r-2 border-t-2", "bottom-4 left-4 border-b-2 border-l-2", "bottom-4 right-4 border-b-2 border-r-2"].map((c) => (
        <span key={c} className={`absolute h-8 w-8 border-[#C9A84C] ${c}`} aria-hidden="true" />
      ))}
      <span className="absolute left-8 top-7 flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-white" aria-hidden="true">
        <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" /> Rec
      </span>
      <span className="absolute bottom-7 right-8 font-mono text-xs tracking-widest text-white/80" aria-hidden="true">06:00:00:00</span>
    </div>
  );
}

function Hero({ img }) {
  return (
    <section className="border-b border-[#2C2C2C]/10 bg-white pb-16 pt-32 lg:pb-24 lg:pt-40" data-testid="catering-hero">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-12 lg:px-8">
        <div className="fade-up lg:col-span-6">
          <p className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-[#9A7B2C]">Catering & production — Feuille de service</p>
          <h1 className="font-sans text-4xl font-semibold leading-[1.05] tracking-tight text-[#2C2C2C] sm:text-5xl lg:text-6xl">Votre production mérite un service à la hauteur.</h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-[#2C2C2C]/70 lg:text-lg">
            Restauration professionnelle pour tournages, productions musicales, événements télévisés, spectacles, manifestations et personnalités. Discret, fiable, ponctuel.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-5">
            <QuoteButton testId="catering-hero-quote" {...Q}>Demander un devis production</QuoteButton>
            <a href="#clients" data-testid="catering-hero-clients" className="inline-flex items-center gap-2 text-sm font-medium text-[#2C2C2C] underline-offset-4 hover:underline">Nos clients <ArrowRight size={15} /></a>
          </div>
          <dl className="mt-12 grid grid-cols-3 border-t border-[#2C2C2C]/15 pt-6 font-mono text-xs uppercase tracking-wider text-[#2C2C2C]/55">
            <div><dt>Plateaux</dt><dd className="mt-1 text-[#2C2C2C]">TV · Ciné · Pub</dd></div>
            <div><dt>Scènes</dt><dd className="mt-1 text-[#2C2C2C]">Concerts · Album</dd></div>
            <div><dt>Terrains</dt><dd className="mt-1 text-[#2C2C2C]">Sport · Public</dd></div>
          </dl>
        </div>
        <div className="fade-up lg:col-span-6"><Viewfinder src={img} alt="Catering de tournage : buffet pour l'équipe technique sur un plateau de cinéma" /></div>
      </div>
    </section>
  );
}

function Clients() {
  return (
    <section id="clients" className="bg-[#FAF6EF] py-20 lg:py-28" data-testid="catering-clients">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="reveal mb-12 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#9A7B2C]">01 — Nos clients cibles</p>
            <h2 className="mt-3 max-w-2xl text-3xl font-semibold tracking-tight text-[#2C2C2C] lg:text-4xl">Traiteur de production audiovisuelle, musicale et sportive</h2>
          </div>
          <p className="max-w-md text-sm text-[#2C2C2C]/65">Du plateau TV au vestiaire, nous nourrissons ceux qui font le spectacle — devant comme derrière la caméra.</p>
        </div>
        <div className="grid border-l border-t border-[#2C2C2C]/15 sm:grid-cols-2 lg:grid-cols-4">
          {CLIENTS.map((c, i) => (
            <div key={c.title} className="reveal group border-b border-r border-[#2C2C2C]/15 bg-white p-7 transition-colors duration-300 hover:bg-[#2C2C2C]" style={{ transitionDelay: `${i * 40}ms` }} data-testid={`catering-client-${i}`}>
              <div className="flex items-center justify-between">
                <c.icon size={24} strokeWidth={1.4} className="text-[#C9A84C]" />
                <span className="font-mono text-xs text-[#2C2C2C]/40 group-hover:text-white/40">{String(i + 1).padStart(2, "0")}</span>
              </div>
              <h3 className="mt-8 text-lg font-semibold leading-snug text-[#2C2C2C] group-hover:text-white">{c.title}</h3>
              <p className="mt-3 font-mono text-[11px] uppercase tracking-wider text-[#9A7B2C] group-hover:text-[#C9A84C]">{c.kw}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Offers({ img }) {
  return (
    <section className="bg-white py-20 lg:py-28" data-testid="catering-offers">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <p className="reveal font-mono text-xs uppercase tracking-[0.3em] text-[#9A7B2C]">02 — Ce que nous proposons</p>
            <h2 className="reveal mt-3 text-3xl font-semibold tracking-tight text-[#2C2C2C] lg:text-4xl">Catering plateau, loges et coulisses</h2>
            <p className="reveal mt-5 text-base text-[#2C2C2C]/70">Catering de production télévision, restauration de tournage cinéma, catering d'artiste ou d'événement musical : une organisation pensée pour le rythme de la production.</p>
            <div className="reveal mt-8"><Viewfinder src={img} alt="Catering backstage de concert dans la loge des artistes" /></div>
          </div>
        </div>
        <ol className="lg:col-span-7">
          {OFFERS.map((o, i) => (
            <li key={o.title} className="reveal grid grid-cols-[auto_1fr] gap-6 border-b border-[#2C2C2C]/10 py-7 first:pt-0" style={{ transitionDelay: `${i * 50}ms` }} data-testid={`catering-offer-${i}`}>
              <span className="pt-1 font-mono text-xs tracking-widest text-[#C9A84C]">{`T+${String(i * 2).padStart(2, "0")}:00`}</span>
              <div>
                <h3 className="text-xl font-semibold text-[#2C2C2C]">{o.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#2C2C2C]/65">{o.text}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Why({ img }) {
  return (
    <section className="bg-[#2C2C2C] py-20 lg:py-28" data-testid="catering-why">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="reveal grid gap-10 lg:grid-cols-2 lg:items-end">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#C9A84C]">03 — Pourquoi Afro Regal</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-white lg:text-4xl">Le traiteur des équipes qui n'ont pas droit à l'erreur</h2>
          </div>
          <p className="text-base text-white/65">Catering sportif pour les équipes professionnelles, traiteur de manifestations, de plateaux ou de personnalités : nous savons qu'un service en retard peut coûter une journée de production.</p>
        </div>
        <div className="mt-14 grid gap-px overflow-hidden rounded-sm bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {WHY.map((w, i) => (
            <div key={w.title} className="reveal bg-[#2C2C2C] p-8" style={{ transitionDelay: `${i * 60}ms` }} data-testid={`catering-why-${i}`}>
              <w.icon size={22} strokeWidth={1.4} className="text-[#C9A84C]" />
              <h3 className="mt-5 text-lg font-semibold text-white">{w.title}</h3>
              <p className="mt-2 text-sm text-white/60">{w.text}</p>
            </div>
          ))}
        </div>
        <div className="reveal mt-14 overflow-hidden"><img src={img} alt="Catering sportif : repas équilibré pour une équipe sportive professionnelle" className="h-64 w-full object-cover opacity-80 lg:h-80" /></div>
      </div>
    </section>
  );
}

export default function CateringProduction() {
  useReveal();
  const img = useSiteImage();
  return (
    <div data-testid="catering-page">
      <Seo
        title="Catering tournage, plateau TV & production — Traiteur artistes et équipes sportives | Afro Regal"
        description="Catering tournage, catering production télévision, traiteur plateau TV, restauration tournage cinéma, catering événement musical, traiteur sortie album, catering artiste et sportif. Discret, fiable, ponctuel. Halal disponible. Devis production."
      />
      <Hero img={img("catering_hero")} />
      <Clients />
      <Offers img={img("catering_concert")} />
      <Why img={img("catering_sport")} />
      <section className="bg-white py-24 lg:py-32" data-testid="catering-final-cta">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <div className="reveal border-l-4 border-[#C9A84C] pl-6 lg:pl-10">
            <h2 className="text-3xl font-semibold tracking-tight text-[#2C2C2C] lg:text-5xl">Vous préparez un tournage, une production, un événement sportif ou artistique ? Parlons-en.</h2>
            <p className="mt-5 max-w-2xl text-base text-[#2C2C2C]/65">Envoyez-nous votre planning, vos effectifs et vos contraintes : nous revenons vers vous avec une proposition adaptée à votre production. Aucun tarif standard — chaque production est chiffrée sur devis.</p>
            <div className="mt-9"><QuoteButton testId="catering-final-quote" className="px-9 py-4 text-base" {...Q}>Demander un devis production</QuoteButton></div>
          </div>
        </div>
      </section>
    </div>
  );
}
